import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  CheckCircle as CloseIcon,
  RestartAlt as ReopenIcon,
  Undo as ReverseIcon,
  PlaylistAddCheck as ChecklistIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import { ledgerAPI } from '../../services/api';
import {
  normalizeFiscalYear,
  normalizeFiscalYears,
  normalizeFiscalPeriods,
} from '../../utils/ledgerIds';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import ModulePageHeader from '../common/ModulePageHeader';
import { EmptyRecordsState } from '../common/ResponsiveRecordList';
import LedgerFormDialog from './LedgerFormDialog';

const getErrorMessage = (error, fallback = 'Operation failed') => {
  const data = error?.response?.data;
  const message = data?.message || fallback;
  // Surface the ledger's per-field validation detail (forwarded by the
  // Laravel gateway in `errors`) instead of a bare "Validation failed".
  const details = data?.errors;
  if (!details) return message;
  const flat = Array.isArray(details)
    ? details.map((d) => (typeof d === 'string' ? d : JSON.stringify(d)))
    : Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
  return flat.length ? `${message}: ${flat.join('; ')}` : message;
};

// Word-labeled "⋮" row menu - the same pattern as LedgerListPage and the
// newer stewardship tabs, so icon-only rows don't force a hover to learn
// what each close-flow action does.
const RowActionsMenuCell = ({ items = [] }) => {
  const [anchor, setAnchor] = React.useState(null);
  if (!items.length) return null;
  const close = () => setAnchor(null);
  return (
    <Box textAlign="left">
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ elevation: 2, sx: { minWidth: 220 } }}
      >
        {items.map((it, i) => (
          <React.Fragment key={it.label || `item-${i}`}>
            {it.dividerBefore && i > 0 && <Divider />}
            <MenuItem onClick={() => { close(); it.onClick?.(); }} sx={it.danger ? { color: 'error.main' } : undefined}>
              {it.icon && <ListItemIcon>{it.icon}</ListItemIcon>}
              {it.label}
            </MenuItem>
          </React.Fragment>
        ))}
      </Menu>
    </Box>
  );
};

// Gateway envelope: { success, data } where data is the ledger payload
// (single object, array, or paginated { items, ... }).
// Every fiscal object is normalized (see utils/ledgerIds) so components only
// ever touch `.id` - never `fiscal_year_id` / `period_id` directly.
const unwrap = (response) => response?.data?.data ?? response?.data ?? {};
const unwrapList = (response) => {
  const payload = unwrap(response);
  if (Array.isArray(payload)) return payload;
  return payload.items ?? [];
};

const statusColor = (status) => {
  switch ((status || '').toUpperCase()) {
    case 'OPEN':
      return 'success';
    case 'CLOSED':
      return 'warning';
    case 'LOCKED':
      return 'error';
    default:
      return 'default';
  }
};

const toInputDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
};

const YEAR_FORM_FIELDS = [
  { key: 'year_name', label: 'Year name (e.g. FY2025)', required: true },
  { key: 'start_date', label: 'Start date', type: 'date', required: true },
  { key: 'end_date', label: 'End date', type: 'date', required: true },
  { key: 'is_active', label: 'Active year', type: 'switch', md: 12 },
];

const PERIOD_FORM_FIELDS = [
  { key: 'period_name', label: 'Period name', required: true },
  { key: 'start_date', label: 'Start date', type: 'date', required: true },
  { key: 'end_date', label: 'End date', type: 'date', required: true },
  { key: 'notes', label: 'Notes', type: 'textarea', md: 12 },
];

const EMPTY_YEAR = { year_name: '', start_date: '', end_date: '', is_active: true };
const EMPTY_PERIOD = { period_name: '', start_date: '', end_date: '', notes: '' };

const FiscalPeriods = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.LEDGER.CLOSE, ACTIONS.READ);
  const canCreate = can(RESOURCES.LEDGER.CLOSE, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.LEDGER.CLOSE, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.LEDGER.CLOSE, ACTIONS.DELETE);
  const canApprove = can(RESOURCES.LEDGER.CLOSE, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [years, setYears] = useState([]);
  const [yearsLoading, setYearsLoading] = useState(true);
  const [yearSearch, setYearSearch] = useState('');
  const [yearClosedFilter, setYearClosedFilter] = useState('all');
  const [yearId, setYearId] = useState(null);
  const [currentYearId, setCurrentYearId] = useState(null);

  const [periods, setPeriods] = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [periodStatusFilter, setPeriodStatusFilter] = useState('all');

  const [yearDialog, setYearDialog] = useState({ open: false, editing: null });
  const [yearValues, setYearValues] = useState(EMPTY_YEAR);
  const [yearSaving, setYearSaving] = useState(false);

  const [periodDialog, setPeriodDialog] = useState({ open: false, editing: null });
  const [periodValues, setPeriodValues] = useState(EMPTY_PERIOD);
  const [periodSaving, setPeriodSaving] = useState(false);

  const [checklist, setChecklist] = useState({
    open: false,
    period: null,
    items: [],
    enforce: false,
    loading: false,
    saving: false,
  });

  const loadYears = useCallback(async () => {
    try {
      setYearsLoading(true);
      const params = { limit: 100 };
      if (yearSearch.trim()) params.search = yearSearch.trim();
      const [yearsRes, currentRes] = await Promise.allSettled([
        ledgerAPI.getFiscalYears(params),
        ledgerAPI.getCurrentFiscalYear(),
      ]);
      const list = normalizeFiscalYears(
        yearsRes.status === 'fulfilled' ? unwrapList(yearsRes.value) : []
      );
      setYears(list);
      const current =
        currentRes.status === 'fulfilled'
          ? normalizeFiscalYear(unwrap(currentRes.value))
          : null;
      if (current?.id) setCurrentYearId(current.id);
      setYearId((prev) => {
        if (prev && list.some((y) => y.id === prev)) return prev;
        if (!list.length) return null;
        const fallback =
          list.find((y) => y.is_current) ||
          (current?.id && list.find((y) => y.id === current.id)) ||
          list[0];
        return fallback.id;
      });
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch fiscal years'), 'error');
    } finally {
      setYearsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearSearch]);

  const loadPeriods = useCallback(async () => {
    if (!yearId) {
      setPeriods([]);
      return;
    }
    try {
      setPeriodsLoading(true);
      const response = await ledgerAPI.getFiscalPeriods({
        fiscal_year_id: yearId,
        limit: 100,
      });
      setPeriods(normalizeFiscalPeriods(unwrapList(response)));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch fiscal periods'), 'error');
    } finally {
      setPeriodsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId]);

  useEffect(() => {
    if (canRead) loadYears();
  }, [canRead, loadYears]);

  useEffect(() => {
    if (canRead) loadPeriods();
  }, [canRead, loadPeriods]);

  const selectedYear = useMemo(
    () => years.find((y) => y.id === yearId) || null,
    [years, yearId]
  );

  const visibleYears = useMemo(() => {
    if (yearClosedFilter === 'open') return years.filter((y) => !y.is_closed);
    if (yearClosedFilter === 'closed') return years.filter((y) => y.is_closed);
    return years;
  }, [years, yearClosedFilter]);

  const visiblePeriods = useMemo(() => {
    if (periodStatusFilter === 'all') return periods;
    return periods.filter(
      (p) => (p.status || '').toUpperCase() === periodStatusFilter
    );
  }, [periods, periodStatusFilter]);

  const refreshAll = () => {
    loadYears();
    loadPeriods();
  };

  // ---------- Fiscal year CRUD + close flows ----------

  const openYearAdd = () => {
    setYearValues(EMPTY_YEAR);
    setYearDialog({ open: true, editing: null });
  };

  const openYearEdit = (year) => {
    setYearValues({
      year_name: year.year_name || '',
      start_date: toInputDate(year.start_date),
      end_date: toInputDate(year.end_date),
      is_active: year.is_active !== false,
    });
    setYearDialog({ open: true, editing: year });
  };

  const submitYear = async () => {
    if (!yearValues.year_name?.trim()) {
      showSnackbar('Year name is required', 'error');
      return;
    }
    if (!yearValues.start_date || !yearValues.end_date) {
      showSnackbar('Start and end dates are required', 'error');
      return;
    }
    if (new Date(yearValues.end_date) <= new Date(yearValues.start_date)) {
      showSnackbar('End date must be after start date', 'error');
      return;
    }
    try {
      setYearSaving(true);
      const payload = {
        year_name: yearValues.year_name.trim(),
        start_date: yearValues.start_date,
        end_date: yearValues.end_date,
        is_active: Boolean(yearValues.is_active),
      };
      if (yearDialog.editing) {
        await ledgerAPI.updateFiscalYear(yearDialog.editing.id, payload);
        showSnackbar('Fiscal year updated successfully', 'success');
      } else {
        const res = await ledgerAPI.createFiscalYear(payload);
        const created = normalizeFiscalYear(unwrap(res));
        showSnackbar(
          `Fiscal year created with ${(created.periods ?? []).length || 'monthly'} periods`,
          'success'
        );
        if (created?.id) setYearId(created.id);
      }
      setYearDialog({ open: false, editing: null });
      loadYears();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setYearSaving(false);
    }
  };

  const runYearWorkflow = (year, action) => {
    const id = year.id;
    const config = {
      close: {
        title: `Close ${year.year_name || 'fiscal year'}`,
        message:
          'Close the year? All open periods will be closed, income-statement balances moved to Retained Earnings, and balance-sheet balances carried into the next year. This runs as checker.',
        success: 'Fiscal year closed successfully',
        fn: ledgerAPI.closeFiscalYear,
      },
      'reverse-close': {
        title: `Reverse close of ${year.year_name || 'fiscal year'}`,
        message:
          'Reverse the year-end close? Closing entries are voided via reversals and closed periods reopen. Blocked while any period is LOCKED.',
        success: 'Fiscal year close reversed successfully',
        fn: ledgerAPI.reverseCloseFiscalYear,
      },
      reopen: {
        title: `Reopen ${year.year_name || 'fiscal year'}`,
        message:
          'Reopen the year? Closed periods return to OPEN. Blocked while any period is LOCKED.',
        success: 'Fiscal year reopened successfully',
        fn: ledgerAPI.reopenFiscalYear,
      },
    }[action];
    if (!config) return;
    confirm({
      title: config.title,
      message: config.message,
      severity: action === 'close' ? 'error' : 'info',
      onConfirm: async () => {
        try {
          await config.fn(id);
          showSnackbar(config.success, 'success');
          refreshAll();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const askDeleteYear = (year) => {
    confirm({
      title: `Delete ${year.year_name || 'fiscal year'}`,
      message:
        (year.periods?.length || 0) > 0
          ? 'This year still has periods and the API will reject the delete. Remove or detach periods first.'
          : `Delete fiscal year ${year.year_name || ''}? Closed years cannot be deleted.`,
      severity: 'error',
      onConfirm: async () => {
        try {
          await ledgerAPI.deleteFiscalYear(year.id);
          showSnackbar('Fiscal year deleted successfully', 'success');
          loadYears();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  // ---------- Fiscal period CRUD + close flows ----------

  const openPeriodAdd = () => {
    if (!yearId) {
      showSnackbar('Select a fiscal year first', 'error');
      return;
    }
    setPeriodValues(EMPTY_PERIOD);
    setPeriodDialog({ open: true, editing: null });
  };

  const openPeriodEdit = (period) => {
    setPeriodValues({
      period_name: period.period_name || '',
      start_date: toInputDate(period.start_date),
      end_date: toInputDate(period.end_date),
      notes: period.notes || '',
    });
    setPeriodDialog({ open: true, editing: period });
  };

  const submitPeriod = async () => {
    if (!periodValues.period_name?.trim()) {
      showSnackbar('Period name is required', 'error');
      return;
    }
    if (!periodValues.start_date || !periodValues.end_date) {
      showSnackbar('Start and end dates are required', 'error');
      return;
    }
    if (new Date(periodValues.end_date) <= new Date(periodValues.start_date)) {
      showSnackbar('End date must be after start date', 'error');
      return;
    }
    try {
      setPeriodSaving(true);
      if (periodDialog.editing) {
        await ledgerAPI.updateFiscalPeriod(periodDialog.editing.id, {
          period_name: periodValues.period_name.trim(),
          start_date: periodValues.start_date,
          end_date: periodValues.end_date,
          notes: periodValues.notes?.trim() || undefined,
        });
        showSnackbar('Fiscal period updated successfully', 'success');
      } else {
        await ledgerAPI.createFiscalPeriod({
          fiscal_year_id: yearId,
          period_name: periodValues.period_name.trim(),
          start_date: periodValues.start_date,
          end_date: periodValues.end_date,
          is_adjustment: true,
          notes: periodValues.notes?.trim() || undefined,
        });
        showSnackbar('Adjustment period created successfully', 'success');
      }
      setPeriodDialog({ open: false, editing: null });
      loadPeriods();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setPeriodSaving(false);
    }
  };

  const runPeriodAction = (period, action, label, pastTense) => {
    confirm({
      title: `${label} period`,
      message: `Are you sure you want to ${label.toLowerCase()} "${period.period_name || `#${period.period_number ?? ''}`}"?`,
      severity: action === 'reopen' || action === 'unlock' ? 'info' : 'error',
      onConfirm: async () => {
        try {
          const fn = {
            close: ledgerAPI.closePeriod,
            reopen: ledgerAPI.reopenPeriod,
            lock: ledgerAPI.lockPeriod,
            unlock: ledgerAPI.unlockPeriod,
          }[action];
          await fn(period.id);
          showSnackbar(`Period ${pastTense} successfully`, 'success');
          loadPeriods();
          loadYears();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const askDeletePeriod = (period) => {
    confirm({
      title: 'Delete period',
      message: `Delete "${period.period_name || `#${period.period_number ?? ''}`}"? Only OPEN periods without transactions or balances can be deleted.`,
      severity: 'error',
      onConfirm: async () => {
        try {
          await ledgerAPI.deleteFiscalPeriod(period.id);
          showSnackbar('Fiscal period deleted successfully', 'success');
          loadPeriods();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  // ---------- Close checklist ----------

  const openChecklist = async (period) => {
    setChecklist({ open: true, period, items: [], enforce: false, loading: true, saving: false });
    try {
      const res = await ledgerAPI.getPeriodChecklist(period.id);
      const data = unwrap(res);
      setChecklist({
        open: true,
        period,
        items: data.items ?? [],
        enforce: Boolean(data.enforce_before_close),
        loading: false,
        saving: false,
      });
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch close checklist'), 'error');
      setChecklist((prev) => ({ ...prev, loading: false }));
    }
  };

  const toggleChecklistItem = (key) => {
    setChecklist((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.key === key
          ? { ...item, status: item.status === 'COMPLETED' ? 'PENDING' : 'COMPLETED' }
          : item
      ),
    }));
  };

  const saveChecklist = async () => {
    if (!checklist.period) return;
    try {
      setChecklist((prev) => ({ ...prev, saving: true }));
      await ledgerAPI.updatePeriodChecklist(checklist.period.id, {
        items: checklist.items.map((item) => ({ key: item.key, status: item.status })),
      });
      showSnackbar('Checklist updated successfully', 'success');
      setChecklist((prev) => ({ ...prev, open: false }));
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setChecklist((prev) => ({ ...prev, saving: false }));
    }
  };

  // ---------- Row actions (word-labeled ⋮ menus, like LedgerListPage) ----------

  const yearMenuItems = (year) => {
    const items = [];
    if (canUpdate && !year.is_closed) {
      items.push({ icon: <EditIcon fontSize="small" color="primary" />, label: 'Edit year', onClick: () => openYearEdit(year) });
    }
    if (canApprove && !year.is_closed) {
      items.push({ icon: <CloseIcon fontSize="small" color="warning" />, label: 'Close year (posts closing entries)', onClick: () => runYearWorkflow(year, 'close') });
    }
    if (canApprove && year.is_closed) {
      items.push({ icon: <ReverseIcon fontSize="small" color="info" />, label: 'Reverse close (voids closing entries)', onClick: () => runYearWorkflow(year, 'reverse-close') });
      items.push({ icon: <ReopenIcon fontSize="small" color="primary" />, label: 'Reopen year', onClick: () => runYearWorkflow(year, 'reopen') });
    }
    if (canDelete && !year.is_closed) {
      items.push({ dividerBefore: true, icon: <DeleteIcon fontSize="small" color="error" />, label: 'Delete year (only when it has no periods)', danger: true, onClick: () => askDeleteYear(year) });
    }
    return items;
  };

  const periodMenuItems = (p) => {
    const status = (p.status || '').toUpperCase();
    const items = [];
    if (canUpdate && status === 'OPEN') {
      items.push({ icon: <EditIcon fontSize="small" color="primary" />, label: 'Edit period (OPEN only)', onClick: () => openPeriodEdit(p) });
    }
    items.push({ icon: <ChecklistIcon fontSize="small" />, label: 'Close checklist', onClick: () => openChecklist(p) });
    if (canApprove && status === 'OPEN') {
      items.push({ icon: <CloseIcon fontSize="small" color="warning" />, label: 'Close period', onClick: () => runPeriodAction(p, 'close', 'Close', 'closed') });
    }
    if (canApprove && status === 'CLOSED') {
      items.push({ icon: <ReopenIcon fontSize="small" color="primary" />, label: 'Reopen period', onClick: () => runPeriodAction(p, 'reopen', 'Reopen', 'reopened') });
      items.push({ icon: <LockIcon fontSize="small" color="error" />, label: 'Lock period', onClick: () => runPeriodAction(p, 'lock', 'Lock', 'locked') });
    }
    if (canApprove && status === 'LOCKED') {
      items.push({ icon: <UnlockIcon fontSize="small" color="primary" />, label: 'Unlock period', onClick: () => runPeriodAction(p, 'unlock', 'Unlock', 'unlocked') });
    }
    if (canDelete && status === 'OPEN') {
      items.push({ dividerBefore: true, icon: <DeleteIcon fontSize="small" color="error" />, label: 'Delete period', danger: true, onClick: () => askDeletePeriod(p) });
    }
    return items;
  };

  if (!canRead) {
    return <Alert severity="error">You do not have permission to view fiscal periods.</Alert>;
  }

  return (
    <Box>
      <ModulePageHeader
        title="Fiscal Years & Periods"
        subtitle="Create years (monthly periods auto-generate), run year-end close, and open/close/lock periods"
        actions={[
          ...(canCreate
            ? [
                <Button key="add-year" variant="contained" startIcon={<AddIcon />} onClick={openYearAdd}>
                  New Fiscal Year
                </Button>,
              ]
            : []),
          ...(canCreate && yearId
            ? [
                <Button key="add-period" variant="outlined" startIcon={<AddIcon />} onClick={openPeriodAdd}>
                  New Adjustment Period
                </Button>,
              ]
            : []),
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Search years"
                placeholder="Search by year name..."
                value={yearSearch}
                onChange={(e) => setYearSearch(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                size="small"
                options={[
                  { label: 'All years', value: 'all' },
                  { label: 'Open years', value: 'open' },
                  { label: 'Closed years', value: 'closed' },
                ]}
                getOptionLabel={(o) => o.label}
                value={[
                  { label: 'All years', value: 'all' },
                  { label: 'Open years', value: 'open' },
                  { label: 'Closed years', value: 'closed' },
                ].find((o) => o.value === yearClosedFilter)}
                onChange={(_, o) => setYearClosedFilter(o?.value || 'all')}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                renderInput={(params) => <TextField {...params} label="Year status" />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                fullWidth
                size="small"
                options={years}
                loading={yearsLoading}
                getOptionLabel={(y) => y.year_name || `Year ${y.id}`}
                value={years.find((y) => y.id === yearId) || null}
                onChange={(_, y) => setYearId(y ? y.id : null)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Selected fiscal year" />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Fiscal years
          </Typography>
          {visibleYears.length === 0 && !yearsLoading ? (
            <EmptyRecordsState message="No fiscal years found. Create the first year to auto-generate its monthly periods." />
          ) : (
            <DataTable
              columns={['Year', 'Start', 'End', 'Status', 'Periods', 'Actions']}
              data={visibleYears}
              loading={yearsLoading}
              emptyMessage="No fiscal years found."
              renderRow={(y) => {
                const isSelected = y.id === yearId;
                const isCurrent = y.id === currentYearId;
                return (
                  <TableRow
                    key={y.id}
                    hover
                    selected={isSelected}
                    onClick={() => setYearId(y.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <strong>{y.year_name || `Year ${y.id}`}</strong>
                        {isCurrent && <Chip label="Current" color="info" size="small" />}
                        {y.is_active === false && <Chip label="Inactive" size="small" />}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(y.start_date)}</TableCell>
                    <TableCell>{formatDate(y.end_date)}</TableCell>
                    <TableCell>
                      <Chip
                        label={y.is_closed ? 'Closed' : 'Open'}
                        color={y.is_closed ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{y.periods?.length ?? '-'}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenuCell items={yearMenuItems(y)} />
                      {canApprove && (
                        <Typography variant="caption" color="text.secondary">
                          Close flows run as checker
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              }}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Creating a year auto-generates its monthly periods and seeds balances. Closing a year
            closes open periods, posts income-statement closing entries to Retained Earnings, and
            carries balance-sheet balances into the next year.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              Periods{selectedYear ? ` - ${selectedYear.year_name}` : ''}
            </Typography>
            <Autocomplete
              size="small"
              sx={{ minWidth: 200 }}
              options={[
                { label: 'All statuses', value: 'all' },
                { label: 'Open', value: 'OPEN' },
                { label: 'Closed', value: 'CLOSED' },
                { label: 'Locked', value: 'LOCKED' },
              ]}
              getOptionLabel={(o) => o.label}
              value={[
                { label: 'All statuses', value: 'all' },
                { label: 'Open', value: 'OPEN' },
                { label: 'Closed', value: 'CLOSED' },
                { label: 'Locked', value: 'LOCKED' },
              ].find((o) => o.value === periodStatusFilter)}
              onChange={(_, o) => setPeriodStatusFilter(o?.value || 'all')}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(params) => <TextField {...params} label="Status" />}
            />
          </Box>
          {visiblePeriods.length === 0 && !periodsLoading ? (
            <EmptyRecordsState message="No periods found for this fiscal year." />
          ) : (
            <DataTable
              columns={['Period', 'Type', 'Start', 'End', 'Status', 'Actions']}
              data={visiblePeriods}
              loading={periodsLoading}
              emptyMessage="No periods found for this fiscal year."
              renderRow={(p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <span>{p.period_name || `#${p.period_number ?? ''}`}</span>
                      {p.is_adjustment && <Chip label="Adjustment" color="info" size="small" />}
                    </Box>
                    {p.close_mode && (
                      <Typography variant="caption" color="text.secondary">
                        {p.close_mode === 'HARD' ? 'Hard close' : 'Soft close'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{p.period_type || (p.is_adjustment ? 'Adjustment' : 'MONTHLY')}</TableCell>
                  <TableCell>{formatDate(p.start_date)}</TableCell>
                  <TableCell>{formatDate(p.end_date)}</TableCell>
                  <TableCell>
                    <Chip label={p.status || 'OPEN'} color={statusColor(p.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <RowActionsMenuCell items={periodMenuItems(p)} />
                    {canApprove && (
                      <Typography variant="caption" color="text.secondary">
                        Runs as checker
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Regular monthly periods are auto-generated with the year - only adjustment periods can be
            created manually. Only OPEN periods can be edited or deleted, only CLOSED periods can be
            locked, and only LOCKED periods can be unlocked.
          </Typography>
        </CardContent>
      </Card>

      <LedgerFormDialog
        open={yearDialog.open}
        onClose={() => setYearDialog({ open: false, editing: null })}
        title={yearDialog.editing ? 'Edit Fiscal Year' : 'New Fiscal Year'}
        fields={YEAR_FORM_FIELDS}
        values={yearValues}
        onChange={(key, value) => setYearValues((prev) => ({ ...prev, [key]: value }))}
        onSubmit={submitYear}
        saving={yearSaving}
      />

      <LedgerFormDialog
        open={periodDialog.open}
        onClose={() => setPeriodDialog({ open: false, editing: null })}
        title={periodDialog.editing ? 'Edit Fiscal Period' : 'New Adjustment Period'}
        fields={PERIOD_FORM_FIELDS}
        values={periodValues}
        onChange={(key, value) => setPeriodValues((prev) => ({ ...prev, [key]: value }))}
        onSubmit={submitPeriod}
        saving={periodSaving}
      />

      <Dialog open={checklist.open} onClose={() => setChecklist((prev) => ({ ...prev, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>
          Close checklist{checklist.period ? ` - ${checklist.period.period_name}` : ''}
        </DialogTitle>
        <DialogContent>
          {checklist.loading ? (
            <Box py={4} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {checklist.enforce && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  The fiscal policy enforces this checklist - all required items must be completed
                  before the period can be closed.
                </Alert>
              )}
              {checklist.items.length === 0 ? (
                <Typography color="text.secondary">No checklist items configured.</Typography>
              ) : (
                <List>
                  {checklist.items.map((item) => (
                    <ListItem
                      key={item.key}
                      secondaryAction={
                        <Checkbox
                          edge="end"
                          checked={item.status === 'COMPLETED'}
                          disabled={!canUpdate || checklist.saving}
                          onChange={() => toggleChecklistItem(item.key)}
                        />
                      }
                    >
                      <ListItemText
                        primary={`${item.label}${item.required ? ' *' : ''}`}
                        secondary={item.status}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChecklist((prev) => ({ ...prev, open: false }))}>
            Close
          </Button>
          {canUpdate && (
            <Button variant="contained" onClick={saveChecklist} disabled={checklist.loading || checklist.saving}>
              {checklist.saving ? 'Saving...' : 'Save Checklist'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
};

export default FiscalPeriods;
